import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import axios from 'axios';
import Reveal from '../components/Reveal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const JourneySoFar = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [stories, setStories] = useState([]);
  const [metrics, setMetrics] = useState({ members: '120+', age_group: '18-35', cities: '6' });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [storiesRes, metricsRes] = await Promise.all([
          axios.get(`${API}/journey`),
          axios.get(`${API}/metrics`),
        ]);

        setStories(storiesRes.data);
        setMetrics(metricsRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
      setLoading(false);
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (!isAutoPlaying || stories.length === 0) return;

    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % stories.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, stories.length]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % stories.length);
    setIsAutoPlaying(false);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + stories.length) % stories.length);
    setIsAutoPlaying(false);
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
    setIsAutoPlaying(false);
  };

  if (loading) {
    return (
      <div className="dark-container min-h-screen flex items-center justify-center" data-testid="journey-loading">
        <p className="text-white">Loading...</p>
      </div>
    );
  }

  return (
    <div className="dark-container" data-testid="journey-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-pattern opacity-60 pointer-events-none" />
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-radial-blue opacity-50 pointer-events-none" />
        <div className="absolute top-1/4 left-0 w-[400px] h-[400px] bg-radial-teal opacity-40 pointer-events-none" />
        <div className="dark-content-container relative pt-20 pb-24 lg:pt-28 lg:pb-32 text-center">
          <span className="eyebrow mb-6 block animate-fade-in">Story so far</span>
          <h1 className="display-huge text-white mb-6 animate-fade-in-delay-1" data-testid="journey-title">
            Our Journey <span className="text-[#64C8FF]">So Far</span>
          </h1>
          <p className="body-large text-white/65 max-w-2xl mx-auto animate-fade-in-delay-2">
            From our first event to building a thriving community of ambitious individuals, every
            experience has been transformative
          </p>
        </div>
      </section>

      {/* METRICS */}
      <section className="relative surface-1 border-y border-white/5">
        <div className="absolute inset-0 bg-grid-pattern-fine opacity-50 pointer-events-none" />
        <div className="dark-content-container relative py-20 lg:py-32">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-8">
            {[
              { value: metrics.members, label: 'Community Members' },
              { value: metrics.age_group, label: 'Typical Age Group' },
              { value: metrics.cities, label: 'Cities Covered' },
            ].map((m, i) => (
              <Reveal
                key={i}
                delay={i * 120}
                className="text-center md:text-left md:border-l md:border-white/10 md:pl-8 first:md:border-l-0 first:md:pl-0"
                data-testid={`journey-metric-${i}`}
              >
                <p className="metric-number text-[#00FFD1]">{m.value}</p>
                <p className="body-medium text-white/60 mt-3">{m.label}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CAROUSEL */}
      <section className="relative">
        <div className="dark-content-container py-24 lg:py-36">
          <Reveal className="max-w-3xl mb-12">
            <span className="eyebrow mb-4 block">Chapters</span>
            <h2 className="display-large text-white">Moments we've shared</h2>
          </Reveal>

          <Reveal className="relative" delay={120}>
            <div
              className="relative overflow-hidden rounded-3xl border border-white/10"
              onMouseEnter={() => setIsAutoPlaying(false)}
              onMouseLeave={() => setIsAutoPlaying(true)}
            >
              <div
                className="flex transition-transform duration-700 ease-out"
                style={{ transform: `translateX(-${currentSlide * 100}%)` }}
              >
                {stories.map((story) => (
                  <div key={story.id} className="w-full flex-shrink-0">
                    <div className="relative h-[500px] md:h-[640px]">
                      <img
                        src={story.image}
                        alt={story.title}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
                      <div className="absolute bottom-0 left-0 right-0 p-8 md:p-14">
                        <span className="inline-block px-3 py-1.5 rounded-full glass-teal text-[#00FFD1] text-xs font-semibold tracking-[0.2em] uppercase mb-4">
                          {story.date}
                        </span>
                        <h2 className="display-medium text-white max-w-3xl">{story.title}</h2>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <button
                onClick={prevSlide}
                aria-label="Previous slide"
                className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 glass-panel rounded-full flex items-center justify-center hover:bg-[#00FFD1]/15 hover:border-[#00FFD1]/40 transition-all z-10"
                data-testid="journey-prev-btn"
              >
                <ChevronLeft className="text-white" size={22} />
              </button>
              <button
                onClick={nextSlide}
                aria-label="Next slide"
                className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 glass-panel rounded-full flex items-center justify-center hover:bg-[#00FFD1]/15 hover:border-[#00FFD1]/40 transition-all z-10"
                data-testid="journey-next-btn"
              >
                <ChevronRight className="text-white" size={22} />
              </button>
            </div>

            <div className="flex justify-center gap-2 mt-10">
              {stories.map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToSlide(index)}
                  aria-label={`Go to slide ${index + 1}`}
                  className={`h-2 rounded-full transition-all duration-300 ${
                    currentSlide === index
                      ? 'bg-[#00FFD1] w-10 shadow-[0_0_12px_rgba(0,255,209,0.5)]'
                      : 'bg-white/20 hover:bg-white/40 w-2'
                  }`}
                  data-testid={`journey-dot-${index}`}
                />
              ))}
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default JourneySoFar;
